import { RegistrationsMap } from "@/components/apprenticeship/enterprise/map/map";
import { RegistrationsMapSidebar } from "@/components/apprenticeship/enterprise/map/map-sidebar";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { useUser } from "@/providers/user";
import { DataService } from "@/services/data";
import { ApprenticeshipRegistrationMapType } from "@/types/apprenticeship/apprentice";
import { ApprenticeshipEnterpriseType } from "@/types/apprenticeship/enterprise";
import { ApprenticeshipProductType } from "@/types/apprenticeship/product";
import { PaginatedResponse } from "@/types/common";
import { ApprenticeshipMapCenter } from "@/types/dynamic-detail";
import { LoaderCircle } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";

interface DynamicApprenticeshipMapContentProps {
    objectId: string | number;
    map: ApprenticeshipMapCenter;
}

export function DynamicApprenticeshipMapContent({ objectId, map }: DynamicApprenticeshipMapContentProps) {
    const [searchParams] = useSearchParams();
    const { has_any_group } = useUser();
    const is_admin = has_any_group(["app_apprenticeship_admin"]);
    const [showAllCandidates, setShowAllCandidates] = useState(is_admin);
    const [registrations, setRegistrations] = useState<ApprenticeshipRegistrationMapType[]>([]);
    const [products, setProducts] = useState<ApprenticeshipProductType[]>([]);
    const [loading, setLoading] = useState(true);
    const [selectedRegistration, setSelectedRegistration] = useState<ApprenticeshipRegistrationMapType>();
    const [sidebarOpen, setSidebarOpen] = useState(false);

    const company = useMemo(
        () =>
            ({
                id: map.id,
                name: map.name,
                latitude: map.latitude,
                longitude: map.longitude,
                street: map.street,
                city: map.city,
                postal_code: map.postal_code,
                country: map.country,
                product_short_name: map.product_short_name,
            }) as ApprenticeshipEnterpriseType,
        [map]
    );

    useEffect(() => {
        let cancelled = false;

        const load = async () => {
            try {
                setLoading(true);
                const productFilter = searchParams.get("product__in");
                const mapQuery = productFilter ? `?product__in=${encodeURIComponent(productFilter)}` : "";
                const [regs, productsResponse] = await Promise.all([
                    DataService.client.url(`/crm/objects/accounts/${objectId}/map_detail/${mapQuery}`).get().json() as Promise<ApprenticeshipRegistrationMapType[]>,
                    DataService.client.url(`/apprenticeship/products/`).get().json() as Promise<PaginatedResponse<ApprenticeshipProductType>>
                ]);
                if (!cancelled) {
                    setRegistrations(regs);
                    setProducts(productsResponse.results);
                }
            } catch (error) {
                console.error(error);
                if (!cancelled) {
                    setRegistrations([]);
                }
            } finally {
                if (!cancelled) {
                    setLoading(false);
                }
            }
        };

        load();
        return () => {
            cancelled = true;
        };
    }, [objectId, searchParams]);

    const selectRegistration = (registration: ApprenticeshipRegistrationMapType) => {
        setSelectedRegistration(registration);
        setSidebarOpen(true);
    };

    if (loading) {
        return (
            <div className="mt-4 flex items-center gap-2 text-sm text-gray-500">
                <LoaderCircle className="h-5 w-5 animate-spin" />
                Loading...
            </div>
        );
    }

    if (!company.latitude || !company.longitude) {
        return <div className="mt-4 text-sm text-gray-500">Coordonnées GPS non disponibles</div>;
    }

    return (
        <>
            <div>
                {!is_admin && (
                    <div className="flex justify-end">
                        <div className="flex items-center space-x-2 py-2">
                            <Checkbox checked={showAllCandidates} onCheckedChange={checked => setShowAllCandidates(!!checked)} />
                            <div className="space-y-1 leading-none">
                                <Label>Voir tous les candidats</Label>
                            </div>
                        </div>
                    </div>
                )}
                <RegistrationsMap
                    items={registrations}
                    company={company}
                    products={products}
                    showAllCandidates={showAllCandidates}
                    className="h-[calc(100vh-315px)]"
                    onItemClick={selectRegistration}
                    interractive
                />
            </div>
            <RegistrationsMapSidebar
                registration={selectedRegistration}
                company={company}
                open={sidebarOpen}
                onOpenChange={setSidebarOpen}
                isAdmin={is_admin}
            />
        </>
    );
}
